import { createAsyncThunk } from '@reduxjs/toolkit';

import { adaptProductPublicAttributesToStore } from 'src/adapters/serviceToStore/adaptProductPublicAttributesToStore';
import { adaptUnifiedProductAttributesToStore } from 'src/adapters/serviceToStore/adaptUnifiedProductAttributesToStore';
import type { UserState } from 'src/redux/user/slice';
import { fetchProductDetails } from 'src/services/cat-service-api/fetchProductDetails';
import type { CatServiceAPIResponseBody } from 'src/services/cat-service-api/types/CatServiceAPIResponseBody';
import { fetchListingsControlsConfig } from 'src/services/contentstack/fetchListingsControlsConfig';
import { fetchUnifiedProduct } from 'src/services/unified-product-service/fetchUnifiedProduct';
import { logger } from 'src/utils/serverLogger';
import type { RequestProductDetailsPayload } from './types';
import { adaptProductDeliveryEventToStore } from './utils/adaptProductDeliveryEventToStore';
import { defaultMetaRobotsIndexable } from './utils/defaultMetaRobotsIndexable';

interface RequestProductsOptions {
  cookie?: string;
  healthcheck?: boolean;
  previewing?: boolean;
  productURI: string;
}

interface MinimalState {
  user: UserState;
}

export const requestProductDetails = createAsyncThunk<
  RequestProductDetailsPayload,
  RequestProductsOptions
>(
  'product/requestProductDetails',
  async ({ cookie, healthcheck, previewing, productURI }, { getState }) => {
    if (healthcheck) {
      // Dynamically import json file to prevent it being included on the client. Healthcheck request only happens server side.
      const { healthCheckProductDetailsResponseBody } = await import(
        'src/services/cat-service-api/fixtures/healthCheckProductDetailsResponseBody'
      );
      const {
        metaDescription,
        metaRobotsIndexable,
        navigation,
        product: { actualCommissionRate, ...restOfProductData },
      } = healthCheckProductDetailsResponseBody;

      return {
        data: {
          product: restOfProductData,
          navigation,
          metaDescription,
          metaRobotsIndexable,
        } as Omit<CatServiceAPIResponseBody, 'actualCommissionRate'>,
      };
    }

    const {
      user: { currencyCode, deliveryZone },
    } = getState() as MinimalState;

    const [{ data, redirectUrl }, unifiedProduct, listingsControlsConfig] = await Promise.all([
      fetchProductDetails(productURI, {
        cookie,
        currencyCode,
        deliveryZone,
        previewing,
      }),
      fetchUnifiedProduct(productURI).catch((error) => {
        logger.warn(`Failed to fetch unified product for ${productURI}: ${error.message}`);
        return null;
      }),
      fetchListingsControlsConfig(),
    ]);

    if (redirectUrl) {
      return { redirectUrl };
    }

    const unifiedProductAttributes = unifiedProduct
      ? adaptUnifiedProductAttributesToStore(unifiedProduct)
      : {};

    const publicAttributes = unifiedProduct
      ? adaptProductPublicAttributesToStore(unifiedProductAttributes, listingsControlsConfig)
      : [];

    const {
      metaDescription,
      metaRobotsIndexable,
      navigation,
      product: { actualCommissionRate, ...restOfProductData },
    } = data as CatServiceAPIResponseBody;

    // Upsell options (gift note / gift wrapping) are only offered at checkout, not
    // on the PDP. Filtered here, upstream of both the product and basket slices, so
    // no consumer ever sees them. If this becomes an AB test, gate the filter on
    // `getState().user.featureFlags` (see src/featureFlags/featureFlags.ts).
    const product = {
      ...restOfProductData,
      options: restOfProductData.options.filter((option) => option.upsell == null),
    };

    return {
      attributes: unifiedProductAttributes,
      deliveryEvent: restOfProductData.deliveryByEvent
        ? adaptProductDeliveryEventToStore(restOfProductData.deliveryByEvent)
        : null,
      publicAttributes,
      data: {
        product,
        navigation,
        metaDescription,
        metaRobotsIndexable: defaultMetaRobotsIndexable(metaRobotsIndexable),
      } as Omit<CatServiceAPIResponseBody, 'actualCommissionRate'>,
    };
  },
);
